import React, { useState, useEffect } from "react";
import { Switch, Button, Input, message, Flex } from "antd";
import {
  ReloadOutlined,
  CopyOutlined,
  ExportOutlined,
  CheckOutlined,
  MinusCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

type Settings = {
  RowSelector: boolean;
  ShowUniqueRecords: boolean;
  IncludeSubTotal: boolean;
};

const ReportConfig: React.FC = () => {
  const [settings, setSettings] = useState<Settings>({
    RowSelector: false,
    ShowUniqueRecords: false,
    IncludeSubTotal: false,
  });

  const [jsonInput, setJsonInput] = useState(""); // Start empty

  // Automatically update jsonInput whenever settings change
  useEffect(() => {
    setJsonInput(JSON.stringify(settings, null, 2));
  }, [settings]);

  const loadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setSettings(parsed);
      message.success("Settings loaded!");
    } catch (err: any) {
      message.error("Invalid JSON: " + err.message);
    }
  };

  const updateSetting = (key: keyof Settings, value: boolean) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // beautify JSON
  const beautifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed, null, 2); // Pretty print with 2 spaces
    } catch (e) {
      message.error("Invalid JSON format for beautification");
      console.log(e)
      return json; // Return original if parsing fails
    }
  };

  // minify JSON
  const minifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed); // Minified JSON
    } catch (e) {
      message.error("Invalid JSON format for minification");
        console.log(e)
      return json; // Return original if parsing fails
    }
  };

  // Update Query JSON
  const updateQueryJsonInput = () => {
    try {
      const parseddata = JSON.parse(jsonInput);
      const stringified = JSON.stringify(parseddata);
      const query = `Update Report SET Config = '${stringified}' Where Id = ??`;
      navigator.clipboard
        .writeText(query)
        .then(() => {
          message.success("JSON copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy text!");
        });
    } catch (e) {
      message.error("Invalid JSON format");
        console.log(e)
      return jsonInput; // Return original if parsing fails
    }
  };

  // validate JSON
  const validateJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      message.success("JSON is correct format!");
      return JSON.stringify(parsed, null, 2); // Pretty print with 2 spaces
    } catch (e) {
      message.error("Invalid JSON format");
        console.log(e)
      return json; // Return original if parsing fails
    }
  };

  return (
    <div>
      <h3>Report config JSON builder</h3>

      <Input.TextArea
        rows={6}
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        placeholder="Paste JSON here..."
        style={{ resize: "none", width: "100%" }}
      />

      <div style={{ marginTop: 20, marginBottom: 8 }}>
        <Flex gap="small" wrap>
          <Button
            onClick={loadJson}
            color="primary"
            variant="solid"
            icon={<ReloadOutlined />}
          >
            Load JSON
          </Button>

          <Button
            onClick={() => {
              try {
                const parseddata = JSON.parse(jsonInput);
                console.log(parseddata);
                navigator.clipboard.writeText(jsonInput);
                message.success("JSON copied to clipboard!");
              } catch (e) {
                message.error("Invalid JSON format");
                  console.log(e)
                return jsonInput;
              }
            }}
            color="pink"
            variant="solid"
            icon={<CopyOutlined />}
          >
            Copy JSON
          </Button>

          <Button
            onClick={() => {
              const beautified = beautifyJson(jsonInput);
              setJsonInput(beautified);
              message.success("Beautified JSON Done!");
            }}
            color="purple"
            variant="solid"
            icon={<ExportOutlined />}
          >
            Beautify
          </Button>

          <Button
            onClick={() => {
              const minified = minifyJson(jsonInput);
              setJsonInput(minified);
              message.success("Minified JSON Done!");
            }}
            color="orange"
            variant="solid"
            icon={<MinusCircleOutlined />}
          >
            Minify
          </Button>

          <Button
            onClick={() => {
              const validateJsondata = validateJson(jsonInput);
              setJsonInput(validateJsondata);
            }}
            color="danger"
            variant="solid"
            icon={<CheckCircleOutlined />}
          >
            Validate JSON{" "}
          </Button>

          <Button
            onClick={updateQueryJsonInput}
            color="cyan"
            variant="solid"
            icon={<CheckOutlined />}
          >
            Copy JSON Query
          </Button>
        </Flex>
      </div>

      <h3>Settings</h3>
      <div style={{ marginBottom: 10 }}>
        <Flex gap="small" wrap>
          <label>
            RowSelector: <span>(To enable checkbox against each row)</span>
            <Switch
              checked={settings.RowSelector}
              onChange={(val) => updateSetting("RowSelector", val)}
            />
          </label>
        </Flex>
      </div>
      <div style={{ marginBottom: 10 }}>
        <Flex gap="small" wrap>
          <label>
            ShowUniqueRecords: <span>(Not in use)</span>
            <Switch
              checked={settings.ShowUniqueRecords}
              onChange={(val) => updateSetting("ShowUniqueRecords", val)}
            />
          </label>
        </Flex>
      </div>
      <div style={{ marginBottom: 10 }}>
        <Flex gap="small" wrap>
          <label>
            IncludeSubTotal: <span>(Not in use)</span>
            <Switch
              checked={settings.IncludeSubTotal}
              onChange={(val) => updateSetting("IncludeSubTotal", val)}
            />
          </label>
        </Flex>
      </div>
    </div>
  );
};

export default ReportConfig;
